// Shrubs: the MEASURED woody 0.5-3 m band from the bake's shrub crown packs
// (shrubs/<i>_<j>.cpk, CPK1 kind 1, 512 m: crownpack.js decodeCrowns,
// vegtiles.js; no y — a shrub stands on the DRAWN ground, terrain.groundDrawn,
// sampled as trees.js does; every per-shrub choice keys on its position hash)
// (swissSURFACE3D nDSM coverage blocks, rev >= 66) — real hedgerows, riverbank
// scrub, green-alder slopes and alpenrose fields with surveyed heights, not
// procedural garnish. Rendering: ONE InstancedMesh of crossed quads (1 draw
// call), rebuilt on movement with the same deterministic distance thinning as
// the forest; the atlas + regional look are shared with the near-field grass
// bushes (grass.js damps its own procedural bushes when this layer exists).
//
// SEASON (gs-family-shrub S1, APP_FORESTVOL §5): the bush atlas's season page
// (vegpaint.js seasonPage, on device — painted and owner columns alike) +
// snow on THE uSeason (season.js SEASON); no tint, no bare: the shrubs keep
// their leaves. Catch per column: leafy = the broadleaf's, needle = the
// conifer's (crowns.js SNOW_CATCH).
//
// SCOPE WEDGE (focus.js, renderconfig.focus.tiers.<t>.shrubs): through the
// optic, every shrub inside the wedge the main pass does not draw is ONE
// apex-facing quad on the focus layer — trees.js WedgeScatter, sharing this
// layer's material (same program, same atlas, same wind).
import * as THREE from 'three';
import RENDER_CFG from './renderconfig.json';
import { wearBushAtlas, loadBushSprites, shrubCapM } from './grass.js';
import { BUSH_ALPHA, BUSH_ASPECT } from './vegpaint.js';
import { makeCrossedQuads, patchShrubMat, shrubMatrix, setSpritePage, setShrubSeason, poolAtlas } from './vegmat.js';   // shared with vegforest.js
import { run as poolRun } from './pool.js';
import { SNOW_CATCH, shrubStyle, shrubNeedle } from './crowns.js';
import { applySeason, readSea } from './season.js';
import { genLive, onGenChange } from './gensched.js';
import { HANDOFF_SHRUB, HANDOFF_GLSL, setShrubHandoff } from './handoff.js';
import { WedgeScatter, Candidates, writeFacing, makeWedgeQuadGeometry } from './trees.js';
import { VegTiles } from './vegtiles.js';
import { decodeCrowns } from './crownpack.js';
import { Y_NONE, YFrom, yStale, yMoved } from './yfrom.js';

const SCFG = RENDER_CFG.shrubs
  || { near_m: 120, view_m: 280, mid_keep: 0.3, render_cap: 9000 };
const FOCUS_LAYER = (RENDER_CFG.focus && RENDER_CFG.focus.layer) || 5;
const STREAM_M = 48;      // re-ask the tiles around the player every STREAM_M
const RETRY_Y_MS = 1000;  // a shrub left out (no drawn ground under it yet): the set is rebuilt after

export class Shrubs {
  // terrain: the TerrainManager (groundDrawn: the drawn ground a shrub stands on)
  constructor(scene, meta, sea, baseUrl, terrain = null) {
    this.scene = scene;
    this.ground = terrain;
    this.smeta = (meta && meta.trees && meta.trees.shrubs) || null;
    this.last = new THREE.Vector3(1e9, 0, 0);
    this.time = { value: 0 };
    this.cellM = 128;
    this.style = shrubStyle(null);
    this.baseUrl = baseUrl;      // the owner's shrub sprites: veg/<sha>.webp
    this._spriteGen = 0;
    this.run = poolRun;          // the client worker pool (pool.js): the bush atlas
    this._atlasWait = false;     // start() .. the bush atlas landed (nearSettled holds)
    this._sh = null;             // the shrubs block (sprite_leafy / sprite_needle)
    const veg = meta && meta.vegetation;
    // no shrub above the world's shrub cap (grass.js shrubCapM: the owner's
    // 2400 m default, vegetation.shrubs.max_elev_m per world) — above it the
    // nDSM's 0.5-3 m blocks are boulders on scree / rock, not shrubs
    this.capM = shrubCapM(veg);
    if (veg && veg.shrubs) this._style(veg.shrubs);
    // the packs (vegtiles.js): world-wide arrays filled pack by pack (slots
    // start..start+n of each), a loaded shrub never moves
    this.byCell = new Map();     // 128 m cell -> shrub slots (a cell lies in one tile)
    if (this.smeta && this.smeta.count) {
      const cm = meta.crowns && meta.crowns.shrubs;
      if (!cm || cm.layout !== 'CPK1') throw new Error('world meta.crowns.shrubs (CPK1 crown pack) missing: rebuild this world (Build)');
      this.tiles = new VegTiles(cm, baseUrl, 5, (t, ab) => this._tile(t, ab));
      const n = this.tiles.count;
      this.xyz = new Float32Array(n * 3);   // y: the drawn ground, NaN until sampled
      this.yd = new Float32Array(n);        // the eye distance y was sampled from
      this.yk = new Uint8Array(n);          // the drawn level it was read on (Y_NONE: none yet)
      this.hgt = new Uint8Array(n);
      this.appear = new Uint8Array(n);
      this.key = new Uint32Array(n);        // crownHash >>> 12: every per-shrub choice
    } else {
      this.tiles = null;
    }
    this._at = new THREE.Vector3(1e9, 0, 0);
    this._atT = 0;
    // THE world season (a world with shrubs and no forest still snows them;
    // applying the same live block twice is the same uSeason)
    this.sea = readSea(sea);     // the world's season data (SEA1, season.js; main.js boot)
    onGenChange((kinds) => { if (kinds.has('uniform') || kinds.has('regen')) this._applySeason(); });
  }

  _applySeason() {
    const live = genLive();
    if (!(live && live.season)) return;
    try { applySeason(live.season, this.sea); } catch (e) { console.error('[shrubs] season:', e.message); }
  }

  // main.js starts the shrubs in its load phase 1 (as Forest.start): the mesh
  // and material, no data; the tiles come through stream() / retarget()
  start() {
    if (!this.tiles || this._started) return Promise.resolve();
    this._started = true;
    this._setup();
    return Promise.resolve();
  }

  // Forest.stream's twin: the draw range 'near', one tile further 'bulk'
  stream(pos, force = false) {
    if (!this.tiles) return;
    const now = performance.now();
    if (!force && Math.hypot(pos.x - this._at.x, pos.z - this._at.z) < STREAM_M && now - this._atT < 2000) return;
    this._at.set(pos.x, 0, pos.z);
    this._atT = now;
    this.tiles.want(pos.x, pos.z, SCFG.view_m, 'near');
    this.tiles.want(pos.x, pos.z, SCFG.view_m + this.tiles.m, 'bulk');
  }

  // (+ the bush atlas landed: the layer draws only then)
  nearSettled(pos) { return !this.tiles || (!this._atlasWait && this.tiles.settled(pos.x, pos.z, SCFG.view_m)); }

  // the Map's view (maplayers.js paintShrubs): its tiles, bulk
  wantRect(rect) { if (this.tiles) this.tiles.wantRect(rect); }

  // one pack landed: its shrubs fill slots start..start+n. decodeCrowns
  // (crownpack.js, pure) throws on a foreign / malformed pack; h = height
  // m·50, appear = the ortho brightness (255 = none)
  _tile(t, ab) {
    const d = decodeCrowns(ab);
    if (d.kind !== 1 || d.i !== t.i || d.j !== t.j || d.n !== t.n) throw new Error(`pack holds ${d.kind} ${d.i}_${d.j} n ${d.n}`);
    const s = t.start, C = this.cellM, cells = new Map();
    for (let k = 0; k < t.n; k++) {
      const i = s + k;
      this.xyz[i * 3] = d.x[k];
      this.xyz[i * 3 + 1] = NaN;
      this.xyz[i * 3 + 2] = d.z[k];
      this.key[i] = d.hash[k] >>> 12;
      const c = Math.floor(d.x[k] / C) + '_' + Math.floor(d.z[k] / C);
      let a = cells.get(c);
      if (!a) cells.set(c, a = []);
      a.push(i);
    }
    this.yd.fill(Infinity, s, s + t.n);
    this.yk.fill(Y_NONE, s, s + t.n);
    this.hgt.set(d.h, s);
    this.appear.set(d.appear, s);
    for (const [c, a] of cells) this.byCell.set(c, Int32Array.from(a));
    this._dataGen = (this._dataGen || 0) + 1;   // indices changed: wedge set re-derives
    this.last.set(1e9, 0, 0);
  }

  // shrub i's ground y seen from distance d (trees.js Forest._yAt): sampled
  // again once the eye halved the distance or a finer node is drawn under its
  // read (yfrom.js yStale); NaN while no node is drawn there. from: the set's
  // coarse reads (YFrom, optional)
  _yAt(i, d, from = null) {
    if (!this.ground) return this.xyz[i * 3 + 1];
    const x = this.xyz[i * 3], z = this.xyz[i * 3 + 2];
    if (d < this.yd[i] * 0.5 || !Number.isFinite(this.xyz[i * 3 + 1]) || yStale(this.ground, x, z, this.yk[i])) {
      const y = this.ground.groundDrawn(x, z, _AT);
      if (Number.isFinite(y)) { this.xyz[i * 3 + 1] = y; this.yd[i] = Math.max(d, 1); this.yk[i] = _AT.k; }
    }
    if (from) from.add(this.yk[i], x, z);
    return this.xyz[i * 3 + 1];
  }

  _style(sh) {
    this._sh = sh;
    this.style = shrubStyle(sh, this.style);
  }

  // the bush atlas is painted in the pool (vegmat.js poolAtlas; grass.js
  // wearBushAtlas): the material draws once it landed
  _setup() {
    const a = this._bushAtlas();
    const mat = new THREE.MeshBasicMaterial({
      map: a.tex,
      alphaTest: BUSH_ALPHA, side: THREE.DoubleSide, fog: true,
    });
    mat.visible = false;
    this._patch(mat);
    setSpritePage(mat, a, BUSH_ALPHA).catch((err) => console.warn('[season page]', err));   // async: after the paint, in the pool
    setShrubSeason(mat, SNOW_CATCH.broadleaf, SNOW_CATCH.conifer);
    this._applySeason();
    this._atlasWait = true;
    a.ready.catch(() => {}).then(() => { this._atlasWait = false; });   // (a failed paint: wearBushAtlas warns)
    wearBushAtlas(this, mat, a, this._sh);
    const geo = makeCrossedQuads();
    this.aVar = new THREE.InstancedBufferAttribute(new Float32Array(SCFG.render_cap), 1);
    geo.setAttribute('aVar', this.aVar);
    this.mesh = new THREE.InstancedMesh(geo, mat, SCFG.render_cap);
    this.mesh.count = 0;
    this.mesh.frustumCulled = false;
    this.mesh.name = 'shrubs';
    this.mesh.matrixAutoUpdate = false;   // static: the instances carry the placement
    this.scene.add(this.mesh);
    // far shrubs in the scope wedge: one slot, this material
    this.wedge = new WedgeScatter(this.scene, FOCUS_LAYER, makeWedgeQuadGeometry);
    this.wedge.reset([mat]);
    this._dataGen = (this._dataGen || 0) + 1;
    this.last.set(1e9, 0, 0);
    this.ready = true;
  }

  // restyle from the bake's vegetation block (same source as grass/forest)
  applyVegetation(veg) {
    this.capM = shrubCapM(veg);
    this.last.set(1e9, 0, 0);
    this._dataGen = (this._dataGen || 0) + 1;
    if (!(veg && veg.shrubs)) return;
    this._style(veg.shrubs);
    if (!this.ready) return;
    const a = this._bushAtlas();
    setSpritePage(this.mesh.material, a, BUSH_ALPHA).catch((err) => console.warn('[season page]', err));
    wearBushAtlas(this, this.mesh.material, a, this._sh);   // the material wears it once it landed
    this.last.set(1e9, 0, 0);
    this._dataGen++;              // needle share may have changed: wedge aVar
  }

  // R8h2 world swap (worldswap.js vegHook): the new meta.vegetation, whose
  // sprite references alone moved — the shrubs block's sprites go onto the
  // atlas the material wears (or is about to), as wearBushAtlas does at boot
  reloadSprites(veg) {
    if (!(veg && veg.shrubs)) return;
    this._style(veg.shrubs);
    if (!this.ready) return;
    const mat = this.mesh.material;
    loadBushSprites(this, mat, mat.userData.atlasWant, this.baseUrl, this._sh);
  }

  _bushAtlas() { return poolAtlas(this.run, 'bush', { color: this.style.color, bloom: this.style.bloom, S: 128 }); }

  // focus (optional): the scope wedge — far shrubs on the focus layer
  update(pos, focus) {
    this.stream(pos);
    if (!this.ready) return;
    this.time.value = performance.now() / 1000;
    this._updateWedge(pos, focus);
    if (this._missAt && performance.now() - this._missAt >= RETRY_Y_MS) { this._missAt = 0; this.last.set(1e9, 0, 0); }
    // a read off a coarse node a finer one replaced (yfrom.js, FT1): the pass again
    if (yMoved(this._y, this.ground, performance.now(), RETRY_Y_MS)) this.last.set(1e9, 0, 0);
    if (pos.distanceTo(this.last) < 48) return;
    this.last.copy(pos);
    const from = new YFrom();
    let missing = 0;
    const c0x = Math.floor((pos.x - SCFG.view_m) / this.cellM);
    const c1x = Math.floor((pos.x + SCFG.view_m) / this.cellM);
    const c0z = Math.floor((pos.z - SCFG.view_m) / this.cellM);
    const c1z = Math.floor((pos.z + SCFG.view_m) / this.cellM);
    const M = _M;
    let n = 0;
    for (let cz = c0z; cz <= c1z && n < SCFG.render_cap; cz++) {
      for (let cx = c0x; cx <= c1x && n < SCFG.render_cap; cx++) {
        const idx = this.byCell.get(cx + '_' + cz);
        if (!idx) continue;
        for (const i of idx) {
          if (n >= SCFG.render_cap) break;
          const x = this.xyz[i * 3], z = this.xyz[i * 3 + 2];
          const d = Math.hypot(x - pos.x, z - pos.z);
          if (d > SCFG.view_m) continue;
          const key = this.key[i];
          if (!_keep(key, d)) continue;
          if (!Number.isFinite(this._yAt(i, d, from))) { missing++; continue; }
          if (this.xyz[i * 3 + 1] > this.capM) continue;
          const h = Math.max(0.5, this.hgt[i] / 50);
          shrubMatrix(M, x, this.xyz[i * 3 + 1], z, key, h);
          this.mesh.setMatrixAt(n, M);
          // leafy vs needle column: the regional needle share, hashed per
          // instance so mixed slopes read mottled, not banded
          this.aVar.setX(n, shrubNeedle(key, this.style.needle) ? 1 : 0);
          n++;
        }
      }
    }
    if (missing && !this._missAt) this._missAt = performance.now();
    this._y = { from, gen: this.ground && Number.isFinite(this.ground.gen) ? this.ground.gen : 0, at: performance.now() };
    this.mesh.count = n;
    this.mesh.instanceMatrix.needsUpdate = true;
    this.aVar.needsUpdate = true;
  }
}

// deterministic per-instance thinning by distance — the main pass draws the
// shrub of key k (its position hash) at viewer distance d <= view_m iff this
// holds (gs-family-shrub turns it into a fade band, APP_FORESTVOL §7)
function _keep(i, d) {
  const t = d <= SCFG.near_m ? 0
    : Math.min(1, (d - SCFG.near_m) / (SCFG.view_m - SCFG.near_m));
  const p = 1 - t * (1 - SCFG.mid_keep);
  return ((i * 2246822519 >>> 6) % 1000) / 1000 < p;
}

Shrubs.prototype._updateWedge = function _updateWedge(pos, focus) {
  const blk = focus && focus.active ? focus.shrubs : null;
  if (!blk) { this.wedge.hide(); return; }
  const key = `${focus.gen}:${focus.zoomGen}:${this._dataGen}`;
  if (key !== this._wedgeKey) {
    this._wedgeKey = key;
    this.tiles.wantWedge(focus, blk.range_m);   // the wedge's tiles, bulk by distance
  }
  this.wedge.step(key, () => this._wedgeJob(pos.x, pos.z, focus, blk));
};

// trees.js Forest._wedgeJob, for the 128 m shrub cells: in the wedge, not
// drawn by the main pass at the player's position, >= min_px tall; over the
// cap the largest pixel heights win (hash-jittered, stable)
Shrubs.prototype._wedgeJob = function* _wedgeJob(px, pz, focus, blk) {
  const ox = focus.ox, oz = focus.oz, ax = focus.ax, az = focus.az;
  const range = blk.range_m, r2 = range * range;
  const cosH = Math.cos(Math.min(Math.PI, focus.halfRad));
  const pxRad = (focus.vfov * Math.PI / 180) / focus.rtPx;
  const C = this.cellM, hd = C * 0.7072;
  const cand = new Candidates();
  const x0 = Math.floor((ox - range) / C), x1 = Math.floor((ox + range) / C);
  const z0 = Math.floor((oz - range) / C), z1 = Math.floor((oz + range) / C);
  let tick = 0;
  for (let cz = z0; cz <= z1; cz++) {
    for (let cx = x0; cx <= x1; cx++) {
      const idx = this.byCell.get(cx + '_' + cz);
      if (!idx || !focus.hit((cx + 0.5) * C, (cz + 0.5) * C, hd, range)) continue;
      for (const i of idx) {
        if (++tick >= 2048) { tick = 0; yield; }
        const x = this.xyz[i * 3], z = this.xyz[i * 3 + 2];
        const dx = x - ox, dz = z - oz, d2 = dx * dx + dz * dz;
        if (d2 > r2) continue;
        const d = Math.sqrt(d2);
        if (dx * ax + dz * az < d * cosH) continue;
        const pd = Math.hypot(x - px, z - pz), key = this.key[i];
        if (pd <= SCFG.view_m && _keep(key, pd)) continue;
        if (!Number.isFinite(this._yAt(i, pd)) || this.xyz[i * 3 + 1] > this.capM) continue;
        const p = Math.max(0.5, this.hgt[i] / 50) / (Math.max(1, d) * pxRad);
        if (p < blk.min_px) continue;
        cand.push(i, p * (0.75 + 0.5 * (((key * 2246822519 >>> 6) % 1000) / 1000)));
      }
    }
  }
  const sel = yield* cand.top(blk.cap);
  const st = { n: 0, m: new Float32Array(sel.length * 16), v: new Float32Array(sel.length) };
  for (let k = 0; k < sel.length; k++) {
    if (++tick >= 2048) { tick = 0; yield; }
    const i = sel[k];
    const h = Math.max(0.5, this.hgt[i] / 50);
    writeFacing(st.m, st.n * 16, this.xyz[i * 3], this.xyz[i * 3 + 1] - 0.08, this.xyz[i * 3 + 2], ox, oz, h * BUSH_ASPECT, h);
    st.v[st.n] = shrubNeedle(this.key[i], this.style.needle) ? 1 : 0;
    st.n++;
  }
  return [st];
};

const _M = new THREE.Matrix4();
const _AT = { k: Y_NONE };   // groundDrawn's level out (_yAt)

// atlas column select + gentle wind + sharpened alpha + the look: vegmat.js
Shrubs.prototype._patch = function _patch(mat) {
  patchShrubMat(mat, this.time, { season: true });
  patchShrubHandoff(mat);
};

// The shrub hand-off (handoff.js HANDOFF_SHRUB) — INERT until gs-family-shrub
// S2: N = SHRUB_HO_INERT (1e9), F = 0, no shrub tile (hoTile false), so every
// fragment keeps — the near set (_keep) and the scope wedge's far shrubs
// (same material) draw exactly as before. S2 sets N = shrubs.view_m, F = the
// live shrub.fade_m and reads hoTile from the shrub tap (shrubtap.js) — in the
// same change that deletes the wedge and _keep.
const SHRUB_HO_INERT = 1e9;
setShrubHandoff(SHRUB_HO_INERT, 0);
function patchShrubHandoff(mat) {
  const base = mat.onBeforeCompile, baseKey = mat.customProgramCacheKey();
  mat.onBeforeCompile = (shader, renderer) => {
    base(shader, renderer);
    shader.uniforms.uNear = HANDOFF_SHRUB.uNear;
    shader.uniforms.uFade = HANDOFF_SHRUB.uFade;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec2 vHoXZ;')
      .replace('#include <project_vertex>', `#include <project_vertex>
        {
          vec4 hoP = vec4(transformed, 1.0);
        #ifdef USE_INSTANCING
          hoP = instanceMatrix * hoP;
        #endif
          vHoXZ = (modelMatrix * hoP).xz;
        }`);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>
uniform float uNear, uFade;
varying vec2 vHoXZ;
${HANDOFF_GLSL}`)
      .replace('#include <clipping_planes_fragment>', `#include <clipping_planes_fragment>
  bool hoTile = false;                         // S2: the shrub indirection (shrubtap.js)
  if (!hoSpriteKeep(length(vHoXZ - cameraPosition.xz), hoDither(gl_FragCoord.xy), hoTile)) discard;`);
  };
  mat.customProgramCacheKey = () => baseKey + ':ho';
}
